import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Slide,
  Alert,
  useTheme,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import NotificationGrid from "./NotificationGrid";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import {
  GET_MY_NOTIFICATION_PREFERENCES,
  GET_NOTIFICATION_CADENCES,
  UPSERT_MY_NOTIFICATION_PREFERENCE,
} from "../../../apis/settingsApis/notifications/notificationApis";

const TransitionComponent = (props) => <Slide {...props} direction="left" />;


const MyNotifications = ({ showHeading = true }) => {
  const theme = useTheme();
  const { data, loading, error, refetch } = useQuery(
    GET_MY_NOTIFICATION_PREFERENCES,
    { fetchPolicy: "cache-and-network" }
  );
  const { data: cadenceData } = useQuery(GET_NOTIFICATION_CADENCES);
  const [upsertPreference] = useMutation(UPSERT_MY_NOTIFICATION_PREFERENCE);

  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const rows = data?.getMyNotificationPreferences ?? [];
  const cadences = cadenceData?.getNotificationCadences ?? [];

  const save = async (row, changes) => {
    setSaving(true);
    try {
      const response = await upsertPreference({
        variables: {
          input: {
            templateKey: row.template_key,
            channelKey: row.channel_key,
            enabled: changes.enabled ?? row.enabled,
            cadenceKey: changes.cadenceKey ?? null,
          },
        },
      });

      const result = response?.data?.upsertMyNotificationPreference;
      setSnackbarInfo({
        message:
          result && result.success === false
            ? result.message || "Could not save notification preference"
            : "Notification settings updated",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });

      await refetch();
    } catch (err) {
      console.error("Error saving notification preference:", err);
      setSnackbarInfo({
        message: "Could not save notification preference",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
    setSaving(false);
  };

  if (loading && !data) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error: {error.message}</Alert>;
  }

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "10px",
        border: `1px solid ${theme.customColors.border.subtle}`,
        backgroundColor: theme.customColors.background.main,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
          backgroundColor: theme.customColors.background.cardHeader,
        }}
      >
        {showHeading && (
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: theme.customColors.text.strong }}
          >
            My notifications
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            color: theme.customColors.text.secondary,
            mt: showHeading ? 0.25 : 0,
          }}
        >
          Choose which notifications you receive. These apply only to you.
        </Typography>
      </Box>

      {/* Rows carry their own vertical padding, so this only sets the gutters. */}
      <Box sx={{ px: 3, py: 0.5 }}>
        <NotificationGrid
          rows={rows}
          cadences={cadences}
          showCadence
          disabled={saving}
          onToggleEnabled={(row, enabled) => save(row, { enabled })}
          onChangeCadence={(row, cadenceKey) => save(row, { cadenceKey })}
          emptyMessage="You have no personal notification settings. This usually means your account is not linked to an employee record."
        />
      </Box>

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default MyNotifications;
